/**
 * Damage formulas (Foundry: system/damage/formula.ts and helpers.ts): combine a weapon's base
 * damage, extra dice and modifiers into the formula and breakdown Foundry shows for a success and
 * a critical success. Dice overrides (DamageDice `override`, fatal) apply first. Critical hits
 * double everything not marked critical-only, then add the critical-only parts, as Foundry's
 * default "double damage" rule does.
 */
import { cloneDamageDice, type DamageDice } from "./damage-dice";
import { cloneModifier, signed, type Modifier } from "./modifiers";
import { DAMAGE_DICE_FACES, isDamageDiceFaces, nextDieSize, type DamageDieSize } from "./values";

export interface BaseDamageData {
  damageType: string;
  diceNumber?: number | undefined;
  dieSize?: DamageDieSize | null | undefined;
  modifier?: number | undefined;
  category: "persistent" | "precision" | "splash" | null;
  materials?: string[] | undefined;
  tags?: string[] | undefined;
}

export type DamageKind = "damage" | "healing";

export interface DamageFormulaData {
  base: BaseDamageData[];
  dice: DamageDice[];
  modifiers: Modifier[];
  /** Maximum number of die size increases; weapons allow one. */
  maxIncreases?: number | undefined;
  /** Whether the roll deals damage, heals, or both (a heal spell); damage alone by default. */
  kinds?: DamageKind[] | undefined;
}

export interface AssembledFormula {
  formula: string;
  breakdown: string[];
}

export type Degree = "failure" | "success" | "criticalSuccess";

interface DamagePartial {
  label: string | null;
  dice: { number: number; faces: number } | null;
  modifier: number;
  damageType: string;
  category: "persistent" | "precision" | "splash" | null;
  critical: boolean | null;
  materials?: string[];
}

type DamageTypeMap = Map<string, DamagePartial[]>;

const CATEGORIES = [null, "persistent", "precision", "splash"] as const;

/** English damage type labels, for breakdowns (the Snapshot's `damageTypes` table when given). */
export interface FormulaLabels {
  damageType: (type: string) => string;
  persistent: (type: string) => string;
  splash: string;
}

export const DEFAULT_FORMULA_LABELS: FormulaLabels = {
  damageType: (type) => type.charAt(0).toUpperCase() + type.slice(1),
  persistent: (type) => `Persistent ${type.charAt(0).toUpperCase() + type.slice(1)}`,
  splash: "Splash",
};

/** Foundry's applyDamageDiceOverrides: die increases, then overrides, on the base damage. */
export function applyDamageDiceOverrides(baseEntries: BaseDamageData[], dice: DamageDice[], options: { critical?: boolean | undefined; maxIncreases?: number | undefined } = {}): void {
  const critical = options.critical ?? false;
  const maxIncreases = options.maxIncreases ?? Infinity;
  const overrideDice = dice.filter((d): d is DamageDice & { override: NonNullable<DamageDice["override"]> } => !d.ignored && !!d.override);
  if (overrideDice.length === 0) return;
  for (const base of baseEntries) {
    const adjustments = overrideDice.filter((m) => {
      const outcomeMatches = m.critical === null || (critical && m.critical) || (!critical && !m.critical);
      return outcomeMatches && (!m.damageType || m.damageType === base.damageType);
    });
    const numUpgrades = Math.min(maxIncreases, adjustments.filter((d) => d.override.upgrade).length);
    const numDowngrades = Math.min(maxIncreases, adjustments.filter((d) => d.override.downgrade).length);
    const delta = numUpgrades - numDowngrades;
    for (let i = 0; i < Math.abs(delta); i++) {
      if (base.dieSize) base.dieSize = nextDieSize(base.dieSize, delta > 0 ? "upgrade" : "downgrade");
    }
    for (const adjustment of adjustments) {
      base.damageType = adjustment.override.damageType ?? base.damageType;
      for (const die of dice.filter((d) => /^(?:deadly|fatal)-/.test(d.slug))) die.damageType = adjustment.override.damageType ?? die.damageType;
      base.dieSize = adjustment.override.dieSize ?? base.dieSize;
      base.diceNumber = adjustment.override.diceNumber ?? base.diceNumber;
    }
  }
}

/** Foundry's createDamageFormula for one degree of success. Null for no damage. */
export function createDamageFormula(input: DamageFormulaData, degree: Degree = "success", labels: FormulaLabels = DEFAULT_FORMULA_LABELS): AssembledFormula | null {
  const damage: DamageFormulaData = {
    base: input.base.map((b) => ({ ...b, materials: b.materials ? [...b.materials] : undefined, tags: b.tags ? [...b.tags] : undefined })),
    dice: input.dice.map(cloneDamageDice),
    modifiers: input.modifiers.map((m) => cloneModifier(m, { value: m.value, enabled: m.enabled, ignored: m.ignored })),
    maxIncreases: input.maxIncreases,
  };
  if (degree === "failure") {
    damage.dice = damage.dice.filter((d) => d.category === "splash");
    damage.modifiers = damage.modifiers.filter((m) => m.damageCategory === "splash");
    if (damage.dice.every((d) => !d.enabled) && damage.modifiers.every((m) => !m.enabled)) return null;
  }
  const critical = degree === "criticalSuccess";
  if (damage.base.length === 0) return null;
  applyDamageDiceOverrides(damage.base, damage.dice, { critical, maxIncreases: damage.maxIncreases });

  const typeMap: DamageTypeMap = new Map();
  for (const baseEntry of damage.base) {
    const list = typeMap.get(baseEntry.damageType) ?? [];
    typeMap.set(baseEntry.damageType, list);
    if ((baseEntry.diceNumber && baseEntry.dieSize) || baseEntry.modifier) {
      const { diceNumber, dieSize, damageType } = baseEntry;
      const modifier = baseEntry.modifier ?? 0;
      const diceSection = diceNumber ? `${diceNumber}${dieSize}` : null;
      const label = !diceSection ? String(modifier) : [diceSection, modifier ? Math.abs(modifier) : null].filter((p) => p !== null).join(modifier < 0 ? " - " : " + ");
      const diceFaces = Number(dieSize?.replace("d", ""));
      list.push({
        label,
        dice: diceNumber && isDamageDiceFaces(diceFaces) ? { number: diceNumber, faces: diceFaces } : null,
        modifier,
        critical: null,
        damageType,
        category: baseEntry.category,
        materials: baseEntry.materials ?? [],
      });
    }
  }

  const outcomeMatches = (m: { critical: boolean | null }): boolean => critical || m.critical !== true;

  for (const dice of damage.dice.filter((d) => d.enabled && outcomeMatches(d))) {
    const matchingBase = damage.base.find((b) => b.damageType === dice.damageType) ?? damage.base[0]!;
    const baseDieSize = Number(matchingBase.dieSize?.replace("d", "")) || null;
    const faces = Number(dice.dieSize?.replace("d", "")) || baseDieSize || null;
    const damageType = dice.damageType ?? matchingBase.damageType;
    if (dice.diceNumber > 0 && isDamageDiceFaces(faces)) {
      const list = typeMap.get(damageType) ?? [];
      list.push({
        label: dice.label === "Persistent Damage" ? null : `${dice.label} +${dice.diceNumber}d${faces}`,
        dice: { number: dice.diceNumber, faces },
        modifier: 0,
        damageType,
        category: dice.category,
        critical: dice.critical,
      });
      typeMap.set(damageType, list);
    }
  }

  const bonusableDamage: { damageType: string; category: string | null }[] = [
    ...damage.base,
    ...damage.dice.filter((d) => d.enabled).map((d) => ({ damageType: d.damageType ?? damage.base[0]!.damageType, category: d.category })),
    ...damage.modifiers.filter((m) => m.enabled && m.value > 0 && m.type === "untyped").map((m) => ({ damageType: m.damageType ?? damage.base[0]!.damageType, category: m.damageCategory })),
  ];

  for (const modifier of damage.modifiers.filter((m) => m.enabled && outcomeMatches(m))) {
    const matchingDamage =
      modifier.kind === "modifier"
        ? (bonusableDamage.find((b) => b.damageType === (modifier.damageType ?? b.damageType)) ?? damage.base[0])
        : bonusableDamage.find((b) => b.damageType === (modifier.damageType ?? b.damageType) && b.category === modifier.damageCategory);
    if (!matchingDamage) continue;
    const damageType = modifier.damageType ?? matchingDamage.damageType ?? "untyped";
    const list = typeMap.get(damageType) ?? [];
    list.push({
      label: modifier.label === "Persistent Damage" ? null : `${modifier.label} ${signed(modifier.value)}`,
      dice: null,
      modifier: modifier.value,
      damageType,
      category: modifier.damageCategory as DamagePartial["category"],
      critical: modifier.critical,
    });
    typeMap.set(damageType, list);
  }

  const kinds = new Set<DamageKind>(input.kinds && input.kinds.length > 0 ? input.kinds : ["damage"]);
  const instances = [...instancesFromTypeMap(typeMap, { degree, persistent: false, labels, kinds }), ...instancesFromTypeMap(typeMap, { degree, persistent: true, labels, kinds })];
  if (instances.length === 0) return null;
  return { formula: `{${instances.map((i) => i.formula).join(",")}}`, breakdown: instances.flatMap((i) => i.breakdown) };
}

function groupBy<T, K>(items: T[], key: (item: T) => K): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    const list = out.get(k) ?? [];
    list.push(item);
    out.set(k, list);
  }
  return out;
}

function instancesFromTypeMap(typeMap: DamageTypeMap, { degree, persistent, labels, kinds }: { degree: Degree; persistent: boolean; labels: FormulaLabels; kinds: Set<DamageKind> }): AssembledFormula[] {
  const critical = degree === "criticalSuccess";
  return [...typeMap.entries()].flatMap(([damageType, typePartials]): AssembledFormula[] => {
    const partials = typePartials.filter((p) => (p.category === "persistent") === persistent);
    if (partials.length === 0) return [];
    const groups = groupBy(partials, (p) => p.category);

    const nonCriticalDamage = ((): string | null => {
      const criticalInclusion: (boolean | null)[] = critical ? [null] : [null, false];
      return sumExpression(createPartialFormulas(groups, criticalInclusion), { double: critical });
    })();
    const criticalDamage = critical ? sumExpression(createPartialFormulas(groups, [true, false])) : null;
    const summedDamage = sumExpression([nonCriticalDamage, criticalDamage]);
    const enclosed = ensureValidFormulaHead(summedDamage) || "0";
    if (enclosed === "0" && persistent) return [];

    const flavor = ((): string => {
      const typeFlavor = damageType === "untyped" && !persistent ? [] : [damageType];
      const persistentFlavor = persistent ? ["persistent"] : [];
      const materialFlavor = typePartials.flatMap((p) => p.materials ?? []);
      const kindFlavor = kinds.has("damage") ? (kinds.has("healing") ? ["damage", "healing"] : []) : ["healing"];
      const all = [...typeFlavor, ...persistentFlavor, ...materialFlavor, ...kindFlavor].join(",");
      return all.length > 0 ? `[${all}]` : "";
    })();

    const breakdown = ((): string[] => {
      const flattened = CATEGORIES.flatMap((c) => {
        const inCategory = groups.get(c) ?? [];
        const withLabel = inCategory.filter((e): e is DamagePartial & { label: string } => e.label !== null);
        const leadingTerms = inCategory.filter((p) => p.label === null && (p.modifier || p.dice?.number || inCategory.every((pp) => pp.label === null)));
        if (leadingTerms.length > 0) {
          const append = c === "splash" ? ` ${labels.splash}` : "";
          withLabel.unshift({ ...leadingTerms[0]!, label: createSimpleFormula(leadingTerms) + append });
        }
        return withLabel;
      });
      const breakdownDamage = flattened.filter((d) => d.critical !== true);
      if (critical) breakdownDamage.push(...flattened.filter((d) => d.critical === true));
      if (breakdownDamage.length === 0) return [];
      const damageTypeLabel = breakdownDamage[0]!.category === "persistent" ? labels.persistent(damageType) : labels.damageType(damageType);
      const labelParts = breakdownDamage.map((d) => d.label);
      labelParts[0] = `${labelParts[0]!.replace(/^\s+\+/, "")} ${damageTypeLabel}`;
      return labelParts;
    })();

    const formula = enclosed && flavor ? `${enclosed}${flavor}` : enclosed;
    return formula ? [{ formula, breakdown }] : [];
  });
}

function createPartialFormulas(partials: Map<DamagePartial["category"], DamagePartial[]>, criticalInclusion: (boolean | null)[]): string[] {
  return CATEGORIES.flatMap((category) => {
    const requested = (partials.get(category) ?? []).filter((p) => criticalInclusion.includes(p.critical));
    const expression = createSimpleFormula(requested);
    const term = expression === "0" ? "" : (category === "precision" || category === "splash") && hasOperators(expression) ? `(${expression})` : expression;
    const flavored = term && category && category !== "persistent" ? (term.endsWith("]") ? `(${term})[${category}]` : `${term}[${category}]`) : term;
    return flavored || [];
  });
}

interface PartialTerm {
  modifier: number;
  dice: { number: number; faces: number } | null;
}

/** Combine dice by faces and sum the constants (Foundry: combinePartialTerms). */
export function combinePartialTerms(terms: PartialTerm[]): PartialTerm[] {
  const modifier = terms.reduce((total, p) => total + p.modifier, 0);
  const constantTerm: PartialTerm | null = modifier ? { dice: null, modifier } : null;
  const dice = terms.filter((p): p is PartialTerm & { dice: NonNullable<PartialTerm["dice"]> } => !!p.dice && p.dice.number > 0).sort((a, b) => b.dice.faces - a.dice.faces);
  const byFace = [...groupBy(dice, (t) => t.dice.faces).values()];
  const combinedDice = byFace.map((group) => ({ modifier: 0, dice: { faces: group[0]!.dice.faces, number: group.reduce((sum, t) => sum + t.dice.number, 0) } }));
  const combined = [...combinedDice, ...(constantTerm ? [constantTerm] : [])];
  return combined.length ? combined : [{ dice: null, modifier: 0 }];
}

/** Dice and modifiers to one formula, type and category ignored (Foundry: createSimpleFormula). */
export function createSimpleFormula(terms: PartialTerm[]): string {
  const combined = combinePartialTerms(terms);
  const constant = combined.find((t) => !!t.modifier)?.modifier ?? 0;
  const diceTerms = combined.filter((t): t is PartialTerm & { dice: NonNullable<PartialTerm["dice"]> } => !!t.dice && t.dice.number > 0).map((t) => `${t.dice.number}d${t.dice.faces}`);
  if (diceTerms.length === 0) return String(constant);
  return [diceTerms.join(" + "), Math.abs(constant)].filter((e) => !!e).join(constant > 0 ? " + " : " - ");
}

function sumExpression(terms: (string | null)[], { double = false } = {}): string | null {
  if (terms.every((t) => !t)) return null;
  const summed = terms.filter((p): p is string => !!p).join(" + ").replaceAll(/ \+ -(\d+)/g, " - $1");
  if (summed === "") return null;
  const enclosed = double && hasOperators(summed) ? `(${summed})` : summed;
  return double ? `2 * ${enclosed}` : enclosed;
}

function hasOperators(formula: string | null): boolean {
  return /[-+*/]/.test(formula ?? "");
}

function ensureValidFormulaHead(formula: string | null): string | null {
  if (!formula) return null;
  const isWrapped = /^\(.*\)$/.test(formula);
  const isSimple = /^\d+(d\d+)?$/.test(formula);
  return isWrapped || isSimple ? formula : `(${formula})`;
}

export { DAMAGE_DICE_FACES };
